import React from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Radio,
  Select,
  Typography,
  Row,
  Col,
  Upload,
  Modal,
  Progress,
  Tag,
  Space,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload/interface'; // For file list

import { Operation } from '../models';
import { EncodingOption, LanguageCodeOption } from '../speech';
import { grpc } from '@improbable-eng/grpc-web';
import transcript_pb_grpc, { ServiceError } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb_service';
import transcript_pb, {
  CreateTranscriptRequest,
  InitUploadRequest,
  Transcript,
  TranscriptJob,
  TranscriptMetadata,
} from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import { TRANSCRIPT_SERVICE_HOST, UPLOAD_LIMIT_B } from '../config';
import { withAuth, AuthContextProps } from '../LocalAuthWrapper';
import { fileToArrayBuffer } from '../utils';
import './NewDocument.css';
import JobsSpinner from './JobsSpinner';
import { NewProjectStatus } from '../types';
import ImportMediaUpload from './ImportMediaUpload';
import { durationStringToDuration } from '../helpers';
import { mapFileToUploadFile, uploadGcs } from '../api/upload';

const { Title } = Typography;
const { Option } = Select;

const FileType = transcript_pb.TranscriptMetadata.FileType;
const DataSource = transcript_pb.TranscriptMetadata.DataSource;

const ALLOWED_AUDIO_FILE_EXT = ['.mp3', '.wav', '.ogg', '.webm', '.flac', '.m4a'];
const ALLOWED_VIDEO_FILE_EXT = ['.mp4', '.webm'];

const ALLOWED_FILE_EXT = [...ALLOWED_AUDIO_FILE_EXT, ...ALLOWED_VIDEO_FILE_EXT, '.json'];

function convertMetadataObjectToMetadata(metadata: transcript_pb.TranscriptMetadata.AsObject) {
  const md = new transcript_pb.TranscriptMetadata();
  md.setFileType(metadata.fileType);
  md.setLanguageCode(metadata.languageCode);
  md.setOriginalFilename(metadata.originalFilename);
  md.setOriginalUri(metadata.originalUri);
  md.setSubject(metadata.subject);
  md.setDescription(metadata.description);
  md.setKeywordsList(metadata.keywordsList);
  return md;
}

/// Convert a string array to a "human" readable list
function toHumanList(extensions: Array<string>, lastJoiner?: string): string {
  if (extensions.length === 0) {
    return '';
  }
  if (extensions.length === 1) {
    return extensions[0];
  }
  return extensions.slice(0, -1).join(', ') + (lastJoiner ? ` ${lastJoiner} ` : ' eða ') + extensions.at(-1);
}

export interface SubmitTranscriptFormProps extends AuthContextProps {
  onSubmit?: () => void;
  onSuccess?: (operation: Operation) => void;
  onJobSubmitted?: () => void;
  languageCodes: LanguageCodeOption[];
  encodings: EncodingOption[];
}

interface SubmitTranscriptFormState {
  waiting: boolean;
  status: NewProjectStatus;
  file?: File;
  formData: transcript_pb.SubmitTranscriptJobRequest.AsObject;
  uploadProgress: number;
  fileOkay: boolean;
  importedTranscript?: Transcript;
}

class SubmitTranscriptForm extends React.Component<SubmitTranscriptFormProps, SubmitTranscriptFormState> {
  static initialFormData = {
    metadata: {
      dictation: false,
      fileType: FileType.AUDIO,
      languageCode: '',
      originalFilename: '',
      originalUri: '',
      subject: '',
      description: '',
      keywordsList: [],
      additionalMetadataMap: new Array<[string, string]>(),
      speakersMap: new Array<[string, transcript_pb.SpeakerInfo.AsObject]>(),
      waveformUri: '',
      originalCharLength: 0,
      originalByteLength: 0,
      dataSource: DataSource.FILE_UPLOAD,
    },
    useUri: false,
    uri: '',
  };

  state: SubmitTranscriptFormState = {
    waiting: false,
    formData: SubmitTranscriptForm.initialFormData,
    uploadProgress: 0,
    status: {
      code: 'ready',
    },
    fileOkay: false,
  };

  client: transcript_pb_grpc.TranscriptServiceClient;

  constructor(props: SubmitTranscriptFormProps) {
    super(props);
    this.client = new transcript_pb_grpc.TranscriptServiceClient(TRANSCRIPT_SERVICE_HOST);
  }

  componentDidMount() {
    this.loadDefaultLanguageCode();
  }

  saveDefaultLanguageCode = () => {
    if (!this.state.formData.metadata) return;
    localStorage.setItem(
      'tiro:submittranscript:defaultmetadata:languagecode',
      this.state.formData.metadata.languageCode,
    );
  };

  loadDefaultLanguageCode = () => {
    const savedLangCode = localStorage.getItem('tiro:submittranscript:defaultmetadata:languagecode');
    const code = savedLangCode ? savedLangCode : this.props.languageCodes[0].value;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        metadata: {
          ...(prevState.formData.metadata as transcript_pb.TranscriptMetadata.AsObject),
          languageCode: code,
        },
      },
    }));
  };

  reportFileUploadStatus = (jobName: string, success: boolean): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const blobName: string = `blob/transcript/${jobName.substring('transcriptjob/'.length)}`;

      const req = new transcript_pb.ReportTranscriptJobFileUploadStatusRequest();
      req.setBlobName(blobName);
      req.setUploadSuccessful(success);

      let successResult: boolean = false;
      try {
        this.client.reportTranscriptJobFileUploadStatus(
          req,
          new grpc.Metadata(this.authHeader()),
          (error: ServiceError | null, response: transcript_pb.ReportTranscriptJobFileUploadStatusResponse | null) => {
            if (error) {
              console.error(error);
              this.setState({ status: { code: 'error', ctx: 'file', message: error.message } });
              reject(false);
            } else {
              if (response !== null) {
                successResult = response.getAcknowledged();
                resolve(successResult);
              }
            }
          },
        );
      } catch (e) {
        console.error(e);
        this.setState({ status: { code: 'error', ctx: 'file', message: 'Óþekkt villa kom upp.' } });
        reject(false);
      }
    });
  };

  handleSubmit = async () => {
    this.setState({ waiting: true });

    const { formData, fileOkay, file } = this.state;
    const { useUri, metadata, uri } = formData;

    if (!useUri && !fileOkay) return;

    if (metadata && !useUri) {
      const fileName = metadata.originalFilename.toLowerCase();
      const isAudio = metadata.fileType === transcript_pb.TranscriptMetadata.FileType.AUDIO;
      const isVideo = metadata.fileType === transcript_pb.TranscriptMetadata.FileType.VIDEO;

      const audioError = isAudio && !ALLOWED_AUDIO_FILE_EXT.some((ext) => fileName.includes(ext));
      const videoError = isVideo && !ALLOWED_VIDEO_FILE_EXT.some((ext) => fileName.includes(ext));

      if (audioError || videoError) {
        this.setState({
          status: {
            code: 'error',
            ctx: 'general',
            message: audioError
              ? `Þú valdir myndband! Fyrir hljóðskrár, vinsamlegast veljið aðeins ${toHumanList(
                  ALLOWED_AUDIO_FILE_EXT,
                )} skrár.`
              : `Röng skráartegund valin. Fyrir myndbönd, vinsamlegast veljið aðeins ${toHumanList(
                  ALLOWED_VIDEO_FILE_EXT,
                )} skrár`,
          },
        });
        return;
      }
    }

    if (!!this.state.importedTranscript && file) {
      try {
        // We are importing a transcript/media combo
        const md = this.state.importedTranscript.getMetadata();
        const metadata = this.state.formData.metadata;
        if (md && metadata) {
          this.state.importedTranscript.setMetadata(convertMetadataObjectToMetadata(metadata));
        }

        const createTranscriptReq = new CreateTranscriptRequest();
        createTranscriptReq.setTranscript(this.state.importedTranscript);

        const transcriptName = await this.createTranscript(createTranscriptReq);
        this.setState({
          importedTranscript: undefined,
        });

        const uploadUrl = await this.initUpload(transcriptName);
        await this.handleFileUploadForImport(uploadUrl, file);
        this.setState({
          formData: SubmitTranscriptForm.initialFormData,
        });
      } catch (error: any) {
        console.error(error);
        this.setState({ status: { code: 'error', ctx: 'general', message: error.message } });
      }
    } else {
      // We're submitting a transcription job
      const req = new transcript_pb.SubmitTranscriptJobRequest();
      req.setUseUri(useUri);
      if (useUri) {
        req.setUri(uri);
      }

      if (metadata) {
        const md = convertMetadataObjectToMetadata(metadata);
        md.setDataSource(useUri ? DataSource.URI : DataSource.FILE_UPLOAD);
        req.setMetadata(md);
      }

      try {
        const job = await this.submitJob(req);

        if (!useUri && file) {
          await this.handleFileUpload(job, file);
        } else {
          this.setState({
            status: {
              code: 'success',
              ctx: 'uri',
              message: 'Vefslóð send í greiningu. Fylgstu með ferlinu í vinnslulistanum hér til hliðar.',
            },
            formData: SubmitTranscriptForm.initialFormData,
          });
        }
      } catch (error: any) {
        console.error(error);
        this.setState({ status: { code: 'error', ctx: 'general', message: error.message } });
      }
    }

    this.saveDefaultLanguageCode();
  };

  private authHeader = () => ({ Authorization: `Bearer ${this.props.authCtx.accessToken}` });

  // Helper to wrap gRPC callback in a Promise
  private submitJob(req: any): Promise<TranscriptJob> {
    return new Promise((resolve, reject) => {
      this.client.submitTranscriptJob(req, new grpc.Metadata(this.authHeader()), (err, job) => {
        if (err) reject(err);
        else if (!job) reject(new Error('No job returned'));
        else resolve(job);
      });
    });
  }

  handleFileUpload = async (job: TranscriptJob, file: File) => {
    this.setState({ waiting: true, status: { code: 'ready' } });

    try {
      const buf = await fileToArrayBuffer(file);
      const gcsSignedUrl = job.getGcsSignedUrl();
      await uploadGcs([buf], gcsSignedUrl, (progressPercent) => {
        this.setState({ uploadProgress: Math.round(progressPercent) });
      });

      await this.reportFileUploadStatus(job.getName(), true);
      this.setState({
        status: {
          code: 'success',
          ctx: 'file',
          message: 'Skrá send í greiningu. Fylgstu með ferlinu í vinnslulistanum.',
        },
      });
    } catch (e: any) {
      await this.reportFileUploadStatus(job.getName(), false);
      this.setState({ status: { code: 'error', ctx: 'file', message: e.message }, waiting: false });
    }
  };

  handleFileUploadForImport = async (uploadUrl: string, file: File) => {
    this.setState({ waiting: true, status: { code: 'ready' } });

    try {
      const buf = await fileToArrayBuffer(file);
      await uploadGcs([buf], uploadUrl, (progressPercent) => {
        this.setState({ uploadProgress: Math.round(progressPercent) });
      });
      this.setState({
        status: {
          code: 'success',
          ctx: 'file',
          message: 'Skrám hlaðið inn.',
        },
      });
    } catch (e: any) {
      this.setState({ status: { code: 'error', ctx: 'file', message: e.message } });
    }
  };

  handleUrlRadio = () => {
    this.setState((prevState) => {
      return {
        formData: {
          ...prevState.formData,
          useUri: !prevState.formData.useUri,
        },
      };
    });
  };

  /// Create a transcript on server and return its name
  private createTranscript(req: CreateTranscriptRequest): Promise<string> {
    return new Promise((resolve, reject) => {
      this.client.createTranscript(req, new grpc.Metadata(this.authHeader()), (err, trans) => {
        if (err) reject(err);
        else if (!trans) reject(new Error('no transcript created'));
        else resolve(trans.getName());
      });
    });
  }

  /// Get an upload URL for a transcript by name
  private initUpload(transcriptName: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // 5. Call InitUpload
      const initUploadReq = new InitUploadRequest();
      initUploadReq.setResourceName(transcriptName);
      this.client.initUpload(initUploadReq, new grpc.Metadata(this.authHeader()), (err, res) => {
        if (err) reject(err);
        else if (!res) reject(new Error('no upload url received'));
        else resolve(res.getGcsSignedUrl());
      });
    });
  }

  handleFileChange = async (e: { target: { files: RcFile[] } }) => {
    const files = e.target.files;

    if (files.length !== 1) {
      throw new Error('Can only handle a single file.');
    }

    const extensionFromFile = (file: RcFile) => {
      const parts = file.name.split('.');
      return '.' + parts[parts.length - 1].toLowerCase();
    };

    const file = files[0];
    const extension = extensionFromFile(file);

    if (extension === '.json') {
      // User is trying to upload a serialized transcript, we need to add an Upload for the media file

      // 1. Read JSON file
      const transcript = JSON.parse(await file.text());

      // 2. Verify that it is a (partial) Transcript JSON and contains the required fields
      const hasSegments = transcript?.segments && transcript.segments.length > 0;
      const hasValidWord =
        hasSegments &&
        transcript.segments[0].words &&
        transcript.segments[0].words.length > 0 &&
        transcript.segments[0].words[0].word;
      const hasSegmentTimes = hasSegments && !!transcript.segments[0].startTime && !!transcript.segments[0].endTime;
      // Only checking the first startTime, assuming the rest matches
      const segmentTimesValid =
        hasSegmentTimes &&
        transcript.segments[0].startTime.match &&
        transcript.segments[0].startTime.match(/^[0-9]+(\.[0-9]+)?s$/);
      const isValid = !!(hasSegments && hasValidWord && hasSegmentTimes && segmentTimesValid);

      if (!isValid) {
        this.setState({
          status: {
            code: 'error',
            ctx: 'general',
            message: 'JSON-skrá er ekki á réttu sniði',
          },
          fileOkay: false,
        });

        return;
      }

      // 3. create the Transcript object with the required and optional fields
      const transcriptPb = new Transcript();
      transcript.segments.forEach((seg: { words: []; startTime: string; endTime: string }) => {
        if (!('startTime' in seg && 'endTime' in seg && 'words' in seg)) {
          console.debug('skipping invalid segment');
          return;
        }

        const segPb = transcriptPb.addSegments();

        seg.words.forEach((wordInfo: object) => {
          if (!('word' in wordInfo)) {
            console.debug('skipping invalid word', wordInfo);
            return;
          }
          const wordPb = segPb.addWords();
          wordPb.setWord(wordInfo.word as string);
          if ('startTime' in wordInfo && 'endTime' in wordInfo) {
            wordPb.setStartTime(durationStringToDuration(wordInfo.startTime as string));
            wordPb.setEndTime(durationStringToDuration(wordInfo.endTime as string));
          }
        });
      });

      const md = new TranscriptMetadata();
      if ('metadata' in transcript) {
        if ('subject' in transcript.metadata) {
          md.setSubject(transcript.metadata.subject);
        }
        if ('description' in transcript.metadata) {
          md.setDescription(transcript.metadata.description);
        }
        if ('languageCode' in transcript.metadata) {
          md.setLanguageCode(transcript.metadata.languageCode);
        }
      }
      md.getAdditionalMetadataMap().set('__tiro__:imported_transcript', 'true');
      transcriptPb.setMetadata(md);

      this.setState((prevState) => ({
        importedTranscript: transcriptPb,
        file,
        formData: {
          ...prevState.formData,
          metadata: {
            ...prevState.formData.metadata,
            ...md.toObject(),
          },
        },
      }));
      this.resetStatus();
    } else {
      const fileType = (() => {
        if (ALLOWED_AUDIO_FILE_EXT.includes(extension)) {
          return FileType.AUDIO;
        }

        if (ALLOWED_VIDEO_FILE_EXT.includes(extension)) {
          return FileType.VIDEO;
        }
      })();

      if (!fileType) {
        this.setState({
          status: {
            code: 'error',
            ctx: 'general',
            message: `Leyfilegar skrár eru: ${toHumanList([...ALLOWED_VIDEO_FILE_EXT, ...ALLOWED_AUDIO_FILE_EXT])}`,
          },
        });
        return;
      }

      // We limit upload size to UPLOAD_LIMIT_B bytes.
      const limit: number = Number(UPLOAD_LIMIT_B);
      const fileSize: number = file.size;

      if (fileSize > limit) {
        const bToMb = (bytes: number) => (bytes / 1000000).toFixed(2);
        this.setState({
          status: {
            code: 'error',
            ctx: 'general',
            message: `Valin skrá er yfir leyfilegum mörkum - Skrá: ${bToMb(fileSize)} MB | Mörk: ${bToMb(limit)} MB`,
          },
          fileOkay: false,
        });
        return;
      }

      const originalFilename = file && file.name ? file.name : '';

      this.setState((prevState) => ({
        fileOkay: true,
        file: file,
        formData: {
          ...prevState.formData,
          metadata: {
            ...prevState.formData.metadata,
            originalFilename,
            subject:
              prevState.formData.metadata && prevState.formData.metadata.subject
                ? prevState.formData.metadata.subject
                : originalFilename,
            fileType,
          } as transcript_pb.TranscriptMetadata.AsObject,
          uri: '',
        },
      }));
      this.state.status.code !== 'ready' && this.resetStatus();
    }
  };

  handleRemoveFile = () => {
    this.setState({
      file: undefined,
      fileOkay: false,
      importedTranscript: undefined,
      formData: {
        ...this.state.formData,
        metadata: {
          ...this.state.formData.metadata,
          originalFilename: '',
        } as transcript_pb.TranscriptMetadata.AsObject,
      },
    });
  };

  resetStatus = () => {
    this.setState({ status: { code: 'ready', ctx: undefined, message: undefined } });
  };

  render() {
    const { waiting, status, formData, uploadProgress, fileOkay } = this.state;

    const allowedFileTypes = ALLOWED_FILE_EXT.join(',');

    const renderFileUpload: boolean = waiting && !formData.useUri;

    const getProgressStatus = (): 'success' | 'exception' | 'active' | 'normal' => {
      if (uploadProgress === 100 && status.code === 'success') return 'success';
      if (status.code === 'error') return 'exception';
      if (waiting) return 'active';
      return 'normal';
    };

    const fileList: UploadFile[] = this.state.file ? [mapFileToUploadFile(this.state.file)] : [];

    const progressModalClosable = status.code === 'success' || status.code === 'error';

    const uriValid = !!formData.uri.match(/^https?:\/\/.+/);

    return (
      <div className='new-document-container'>
        <Title level={2}>Nýtt skjal</Title>

        <Modal
          title='Hleð inn skrá...'
          open={renderFileUpload}
          footer={null}
          closable={progressModalClosable}
          maskClosable={progressModalClosable}
          keyboard={progressModalClosable}
          onCancel={() => {
            this.setState({
              file: undefined,
              uploadProgress: 0,
              waiting: false,
              status: { code: 'ready', ctx: undefined, message: undefined },
              formData: SubmitTranscriptForm.initialFormData,
            });
          }}
        >
          <Progress
            percent={uploadProgress}
            status={getProgressStatus()}
            strokeWidth={10}
            showInfo={status.code !== 'success' && status.code !== 'error'}
          />
          <p style={{ textAlign: 'center', marginTop: '10px' }}>
            {status.code === 'success' || status.code === 'error' ? status.message : `Hleð inn...`}
          </p>
          {(status.code === 'success' || status.code === 'error') && (
            <Button
              block
              onClick={() => {
                this.setState(
                  {
                    file: undefined,
                    uploadProgress: 0,
                    waiting: false,
                    status: { code: 'ready', ctx: undefined, message: undefined },
                    formData: SubmitTranscriptForm.initialFormData,
                  },
                  this.loadDefaultLanguageCode,
                );
              }}
            >
              Loka
            </Button>
          )}
        </Modal>

        <Form layout='vertical' onFinish={this.handleSubmit}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label='Hlaða inn skrá eða senda inn vefslóð?'>
                <Radio.Group
                  name='useUri'
                  value={formData.useUri ? 'url' : 'file'}
                  onChange={this.handleUrlRadio}
                  disabled={!!this.state.importedTranscript}
                >
                  <Radio value='url'>Vefslóð</Radio>
                  <Radio value='file'>Skrá</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col flex='auto'>
              <Form.Item
                label={formData.useUri ? 'Vefslóð (URL)' : 'Skrá'}
                required
                validateStatus={
                  status.code === 'error' && (status.ctx === 'general' || status.ctx === 'file') ? 'error' : ''
                }
                help={
                  status.code === 'error' && (status.ctx === 'general' || status.ctx === 'file') ? status.message : null
                }
              >
                {formData.useUri ? (
                  <Input
                    id='url'
                    type='url'
                    placeholder='http://example.com/file.mp3'
                    value={formData.uri}
                    status={uriValid ? '' : 'error'}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const uri: string = e.target.value;
                      this.setState((prevState) => {
                        return {
                          formData: { ...prevState.formData, uri: uri },
                        };
                      });
                    }}
                  />
                ) : (
                  <Upload
                    accept={allowedFileTypes}
                    fileList={fileList}
                    maxCount={1}
                    beforeUpload={async (file) => {
                      const mockEvent = { target: { files: [file] } };
                      await this.handleFileChange(mockEvent);
                      return false; // Prevent AntD from auto-uploading
                    }}
                    onRemove={this.handleRemoveFile}
                  >
                    <Button icon={<UploadOutlined />} disabled={!!this.state.importedTranscript}>
                      Veldu skrá
                    </Button>
                  </Upload>
                )}
              </Form.Item>
            </Col>
          </Row>
          {!!this.state.importedTranscript && (
            <Row>
              <Col span={6}>
                <ImportMediaUpload
                  accept={[...ALLOWED_AUDIO_FILE_EXT, ...ALLOWED_VIDEO_FILE_EXT].join(',')}
                  onFileChange={this.handleFileChange}
                  onRemove={this.handleRemoveFile}
                >
                  Hljóð- eða videoskrá
                </ImportMediaUpload>
              </Col>
              <Col span='flex'>Þú ætlar að flytja inn tilbúna JSON skrá. Þá vantar hljóð- eða videoskrá.</Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label='Titill' required>
                <Input
                  name='subject'
                  value={formData.metadata ? formData.metadata.subject : ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const subject: string = e.target.value;
                    this.setState((prevState) => ({
                      formData: {
                        ...prevState.formData,
                        metadata: {
                          ...prevState.formData.metadata,
                          subject,
                        } as transcript_pb.TranscriptMetadata.AsObject,
                      },
                    }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label='Tungumál (mállíkan)'>
                <Select
                  value={formData.metadata ? formData.metadata.languageCode : ''}
                  onChange={(languageCode: string) => {
                    this.setState((prevState) => ({
                      formData: {
                        ...prevState.formData,
                        metadata: {
                          ...prevState.formData.metadata,
                          languageCode,
                        } as transcript_pb.TranscriptMetadata.AsObject,
                      },
                    }));
                  }}
                >
                  {this.props.languageCodes.map((opt) => (
                    <Option key={opt.key} value={opt.value}>
                      {opt.text}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label='Lýsing'>
            <Input.TextArea
              name='description'
              autoSize={{ minRows: 4, maxRows: 4 }}
              value={formData.metadata ? formData.metadata.description : ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const description: string = e.target.value;
                this.setState((prevState) => ({
                  formData: {
                    ...prevState.formData,
                    metadata: {
                      ...prevState.formData.metadata,
                      description,
                    } as transcript_pb.TranscriptMetadata.AsObject,
                  },
                }));
              }}
            />
          </Form.Item>

          <Form.Item label='Merkimiðar'>
            <Input
              placeholder='Merkimiðar skildir að með kommum (t.d. „fyrirlestur, stærðfræði“ )'
              defaultValue={formData.metadata ? formData.metadata.keywordsList.join(',') : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                this.setState((prevState) => ({
                  formData: {
                    ...prevState.formData,
                    metadata: {
                      ...prevState.formData.metadata,
                      keywordsList: [
                        ...value
                          .split(',')
                          .map((k) => k.replace(/(^[ ]+|[ ]+$)/g, ''))
                          .filter((k) => !k.match(/^[ ]*$/)),
                      ],
                    } as transcript_pb.TranscriptMetadata.AsObject,
                  },
                }));
              }}
            />
            <div style={{ marginTop: '8px' }}>
              {formData.metadata
                ? formData.metadata.keywordsList.map((keyword) => (
                    <Tag color='blue' key={keyword}>
                      {keyword}
                    </Tag>
                  ))
                : null}
            </div>
          </Form.Item>

          <div>
            {status.code === 'error' && status.ctx !== 'file' && (
              <Alert
                type='error'
                closable
                onClose={() => this.resetStatus()}
                message={status.message || 'Óþekkt villa kom upp.'}
                style={{ marginTop: '10px' }}
              />
            )}
            {status.code === 'success' && status.ctx !== 'file' && (
              <Alert
                type='success'
                closable
                onClose={() => this.resetStatus()}
                message={status.message || 'Aðgerð tókst.'}
                style={{ marginTop: '10px' }}
              />
            )}
          </div>
          <div className='bottom-form-div'>
            <Form.Item noStyle>
              <Space>
                <Button
                  type='primary'
                  htmlType='submit'
                  loading={waiting}
                  disabled={
                    waiting ||
                    (!formData.useUri && !fileOkay) ||
                    (formData.useUri && !uriValid) ||
                    formData.metadata?.subject === ''
                  }
                >
                  Senda
                </Button>
              </Space>
              <JobsSpinner />
            </Form.Item>
          </div>
        </Form>
      </div>
    );
  }
}

export default withAuth(SubmitTranscriptForm);
